import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/api/useAppConfig';

/**
 * Replaces `Pages/Dashboard/Index.cshtml` at `/`.
 *
 * A welcome card, not a dashboard in the charting sense: the page model's
 * `IPageRepository` — the thing that would have made it one — is commented out,
 * and the heading and body are strings built from the application name. So this
 * is the same static content, with the name coming from `GET /api/app-config`
 * rather than `IOptions<ApplicationConfiguration>`.
 *
 * The Razor version emitted its body through `@Html.Raw`, which is why the copy
 * lived in the page model as an HTML string. Here it is markup, so there is
 * nothing to trust and nothing to escape.
 */

/** Served from the MysteryCalls RCL's static files. */
const USER_MANUAL_URL = '/mystery/user-manual.pdf';

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: config } = useAppConfig();

  const appName = config?.name || t('Dashboard.FallbackAppName', 'the Dashboard');

  return (
    <div className="container-fluid py-3">
      <div className="mb-3">
        <h1 className="h3 mb-1">{t('Dashboard.Welcome', 'Welcome')}</h1>
        <p className="text-muted mb-0">
          {t('Dashboard.Subtitle', 'Use the sidebar to navigate the menus')}
        </p>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-transparent">
          <h5 className="mb-0">
            {t('Dashboard.Heading', 'Welcome to {{appName}}', { appName })}
          </h5>
        </div>
        <div className="card-body">
          <p className="lead">
            {t(
              'Dashboard.Lead',
              "We're glad to see you here. {{appName}} gives you access to your reports, data and administration tools, all in one place.",
              { appName },
            )}
          </p>
          <p>
            {t('Dashboard.NavigationHintBefore', 'Use the ')}
            <strong>{t('Dashboard.SidebarLabel', 'sidebar on the left')}</strong>
            {t(
              'Dashboard.NavigationHintAfter',
              ' to navigate between the available menus and sections. Click a menu group to expand it and reveal its pages.',
            )}
          </p>

          <hr />

          {/* The Razor page gated this on `User.Identity.IsAuthenticated`; the
              whole route already sits behind <RequireAuth>, so reaching this
              markup at all means the check has passed. */}
          <a
            href={USER_MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            {t('Dashboard.UserManual', 'User Manual')}
          </a>
        </div>
      </div>
    </div>
  );
}
